/**
 * clients-store — interface com /api/v1/clients (Rodada Frente A).
 *
 * React Query nativo: useClientsSearch debounced; useClient(id); useClientNome.
 * SÓ LEITURA desde o B-12 (mig 427): o servidor leu `clients` de `clientes` e
 * removeu POST/PUT/DELETE — as actions daqui saíram na faxina de 13/08.
 */

import { useQuery } from '@tanstack/react-query';

import { api, hasToken } from './api';

export interface Client {
  id: string;
  nome: string;
  /** #571: os dois nomes separados (a escolha do cliente nos Vínculos mostra os dois). Servidor antigo não manda. */
  nomeFantasia?: string | null;
  razaoSocial?: string | null;
  cnpj: string | null;
  email: string | null;
  telefone: string | null;
  endereco: Record<string, unknown> | null;
  ativo: boolean;
  omieId: string | null;
  criadoEmIso: string;
  criadoPor: string | null;
}

// ─── Keys ───────────────────────────────────────────────────────────────

export const CLIENTS_KEYS = {
  all: ['v1', 'clients'] as const,
  search: (q: string) => ['v1', 'clients', 'search', q] as const,
  byId: (id: string) => ['v1', 'clients', 'byId', id] as const,
};

// ─── List / Search ──────────────────────────────────────────────────────

interface UseClientsOptions {
  q?: string;
  includeInactive?: boolean;
  limit?: number;
}

export function useClientsSearch(options: UseClientsOptions = {}) {
  const { q = '', includeInactive = false, limit = 20 } = options;
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (includeInactive) params.set('includeInactive', '1');
  params.set('limit', String(limit));
  const qs = params.toString();

  return useQuery({
    queryKey: ['v1', 'clients', 'search', q, includeInactive, limit] as const,
    queryFn: () => api.get<Client[]>(`/api/v1/clients?${qs}`),
    enabled: hasToken(),
    // Mantém resultado anterior visível durante refetch (UX combobox)
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}

export function useClient(id: string | null) {
  return useQuery({
    queryKey: CLIENTS_KEYS.byId(id ?? ''),
    queryFn: () => api.get<Client>(`/api/v1/clients/${id}`),
    enabled: !!id && hasToken(),
    staleTime: 60_000,
  });
}

/**
 * Resolve um valor cru de `Deal.clienteId` pro nome legível pra renderizar
 * em cards/listas/headers. Trata 3 casos (BUG-V2-001, 2026-05-18):
 *
 *   1. Valor vazio  → '' (caller decide se mostra placeholder ou esconde)
 *   2. Valor que começa com `client-` → lookup via React Query
 *      - Cliente encontrado → `client.nome`
 *      - 404 (órfão / deletado) → `'Cliente não cadastrado'`
 *      - Loading → '' (UI deve mostrar skeleton ou fallback discreto)
 *   3. Qualquer outro valor → o próprio valor (compat com seeds antigos
 *      `'Condomínio Saint Michael'` e dados pré-refactor)
 *
 * Por que hook próprio e não chamar `useClient` direto:
 *   - Centraliza a lógica de detecção legado vs ID, evita duplicação nos
 *     consumers (card do Kanban, linha da lista, header do detail dialog)
 *   - `useClient` faria fetch desnecessário pra valores legados ('Saint
 *     Michael' não é UUID)
 *   - Retorna string direto — caller não precisa lidar com loading/error
 *     state pra um caso de render simples
 *
 * **OK 1 hook por card render** — React Query dedupe a query no cache, então
 * 50 cards apontando pro mesmo clienteId disparam só 1 fetch (e os outros
 * 49 leem do cache).
 */
/** Detecta UUID puro (sem prefix client-). Usado pra lookup em `clientes` V2. */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useClientNome(rawValue: string | undefined | null): string {
  const value = rawValue ?? '';
  const looksLikeV1Id = value.startsWith('client-');
  // BUG-B1.6.2: deals de workflow corretiva têm `cliente=<uuid>` (V2 clientes
  // table), sem prefix `client-`. Fallback pra V2 quando bate pattern UUID.
  const looksLikeV2Id = !looksLikeV1Id && UUID_REGEX.test(value);

  const { data: v1Data, isLoading: v1Loading, isError: v1Error, error: v1Err } = useClient(
    looksLikeV1Id ? value : null,
  );
  // V2 fallback: query GET /api/v1/clientes/:id (admin endpoint, mesmo
  // pattern de useClient). Sem extra hook pra evitar import circular.
  const { data: v2Data } = useQuery({
    queryKey: ['v1', 'clientes', 'single', value] as const,
    queryFn: async () => {
      const r = await api.get<{ razaoSocial: string; nomeFantasia: string | null }>(
        `/api/v1/clientes/${encodeURIComponent(value)}`,
      );
      return r;
    },
    enabled: looksLikeV2Id && hasToken(),
    staleTime: 60_000,
  });

  if (!value) return '';
  if (looksLikeV1Id) {
    if (v1Loading) return '';
    if (v1Error) {
      const isNotFound =
        v1Err instanceof Error && /404|client-not-found|not.?found/i.test(v1Err.message);
      return isNotFound ? 'Cliente não cadastrado' : '';
    }
    return v1Data?.nome ?? 'Cliente não cadastrado';
  }
  if (looksLikeV2Id) {
    if (!v2Data) return ''; // loading or error — empty pra não piscar UUID
    return v2Data.nomeFantasia ?? v2Data.razaoSocial ?? 'Cliente não cadastrado';
  }
  // Legacy: razão social literal (seed antigo "Saint Michael")
  return value;
}

// ─── Actions ────────────────────────────────────────────────────────────

// B-12 (mig 427) matou POST/PUT/DELETE /clients no servidor; o hook
// useClientActions que chamava esses endpoints ficou POSTando pro vazio com
// zero chamadores e saiu na faxina de 13/08 (diagnóstico dos caminhos de
// cadastro). Criar/editar cliente é useClienteV2Actions (clientes-v2-store).
